import React from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile, UploadProps } from 'antd';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { selectUserState } from '../../store/selectors/user';
import {
  apiDeleteMarketingContact,
  apiGetMarketingCampaignContacts,
  apiGetMarketingCampaignSettings,
  apiImportMarketingCampaignCsv,
  apiPauseMarketingCampaign,
  apiSendTestMarketingCampaignEmail,
  apiStartMarketingCampaign,
  apiStopMarketingCampaign,
  apiUpdateMarketingCampaignSettings,
} from '../../services/api/marketing-campaign';
import {
  ApiMarketingCampaignGetSettingsResponse,
  ApiMarketingCampaignUpdateSettingsRequest,
  MarketingCampaignStatus,
  MarketingContact,
  MarketingContactStatus,
} from '../../services/api/types/marketing-campaign';

const DAYS_OF_WEEK = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 0 },
];

interface SettingsFormValue {
  dailyCap: number;
  allowedDaysOfWeek: number[];
  sendWindowStartHour: number;
  sendWindowEndHour: number;
  waveStartDate: dayjs.Dayjs | null;
  deadlineDate: dayjs.Dayjs | null;
}

const STATUS_COLORS: Record<MarketingCampaignStatus, string> = {
  [MarketingCampaignStatus.DRAFT]: 'default',
  [MarketingCampaignStatus.RUNNING]: 'green',
  [MarketingCampaignStatus.PAUSED]: 'orange',
  [MarketingCampaignStatus.STOPPED]: 'red',
};

interface ContactRow extends MarketingContact {
  key: string;
}

const PER_PAGE_LIMIT = 20;
const CAMPAIGN_STEPS = [1, 2, 3, 4];

function EmailCampaign() {
  const { token } = useSelector(selectUserState);
  const [saving, setSaving] = React.useState(false);
  const [data, setData] =
    React.useState<ApiMarketingCampaignGetSettingsResponse | null>(null);
  const [contacts, setContacts] = React.useState<[ContactRow[], number] | null>(
    null,
  );
  const [contactsLoading, setContactsLoading] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<
    MarketingContactStatus | undefined
  >(undefined);
  const [offset, setOffset] = React.useState(0);
  const [refresh, setRefresh] = React.useState<symbol>(Symbol('s'));
  const [form] = Form.useForm<SettingsFormValue>();
  const [csvFileList, setCsvFileList] = React.useState<UploadFile[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [testEmail, setTestEmail] = React.useState('');
  const [sendingTestStep, setSendingTestStep] = React.useState<number | null>(
    null,
  );

  const loadSettings = React.useCallback(async () => {
    if (!token) return;
    const [response, status] = await apiGetMarketingCampaignSettings(token);
    if (status === 200) {
      setData(response);
    } else {
      message.error('Failed to load campaign settings');
    }
  }, [token]);

  React.useEffect(() => {
    loadSettings();
  }, [loadSettings, refresh]);

  React.useEffect(() => {
    if (!token) return;
    setContactsLoading(true);
    (async () => {
      const [response, status] = await apiGetMarketingCampaignContacts(token, {
        status: statusFilter,
        offset,
        limit: PER_PAGE_LIMIT,
      });
      if (status === 200) {
        setContacts([
          response.contacts.map((c) => ({ ...c, key: c.id.toString() })),
          response.total,
        ]);
      }
      setContactsLoading(false);
    })();
  }, [token, statusFilter, offset, refresh]);

  const handleUpdateSettings = React.useCallback(
    async (values: SettingsFormValue) => {
      if (!token || !data) return;
      setSaving(true);

      const body: ApiMarketingCampaignUpdateSettingsRequest = {};
      const { settings } = data;

      if (values.dailyCap !== settings.dailyCap) {
        body.dailyCap = values.dailyCap;
      }
      if (
        JSON.stringify([...values.allowedDaysOfWeek].sort()) !==
        JSON.stringify([...settings.allowedDaysOfWeek].sort())
      ) {
        body.allowedDaysOfWeek = values.allowedDaysOfWeek;
      }
      if (values.sendWindowStartHour !== settings.sendWindowStartHour) {
        body.sendWindowStartHour = values.sendWindowStartHour;
      }
      if (values.sendWindowEndHour !== settings.sendWindowEndHour) {
        body.sendWindowEndHour = values.sendWindowEndHour;
      }
      const waveStartDate = values.waveStartDate?.format('YYYY-MM-DD');
      if (waveStartDate && waveStartDate !== settings.waveStartDate) {
        body.waveStartDate = waveStartDate;
      }
      const deadlineDate = values.deadlineDate?.format('YYYY-MM-DD');
      if (deadlineDate && deadlineDate !== settings.deadlineDate) {
        body.deadlineDate = deadlineDate;
      }

      if (Object.keys(body).length === 0) {
        message.info('No changes detected!');
        setSaving(false);
        return;
      }

      const [, status] = await apiUpdateMarketingCampaignSettings(token, body);
      if (status === 200 || status === 201) {
        message.success('Campaign settings updated!');
        setRefresh(Symbol('s'));
      } else {
        message.error('Failed to update campaign settings');
      }
      setSaving(false);
    },
    [token, data],
  );

  const handleStart = React.useCallback(() => {
    Modal.confirm({
      title: 'Start the campaign?',
      content:
        'This will begin sending emails to pending contacts on the next sweep, within the configured daily cap and send window.',
      okText: 'Start',
      onOk: async () => {
        if (!token) return;
        const [, status] = await apiStartMarketingCampaign(token);
        if (status === 200 || status === 201) {
          message.success('Campaign started');
          setRefresh(Symbol('s'));
        } else {
          message.error('Failed to start campaign');
        }
      },
    });
  }, [token]);

  const handlePause = React.useCallback(async () => {
    if (!token) return;
    const [, status] = await apiPauseMarketingCampaign(token);
    if (status === 200 || status === 201) {
      message.success('Campaign paused');
      setRefresh(Symbol('s'));
    } else {
      message.error('Failed to pause campaign');
    }
  }, [token]);

  const handleStop = React.useCallback(() => {
    Modal.confirm({
      title: 'Stop the campaign?',
      content:
        'This is a hard stop, distinct from pausing. All pending sends will be cancelled and the campaign will need to be reconfigured to resume.',
      okText: 'Stop',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!token) return;
        const [, status] = await apiStopMarketingCampaign(token);
        if (status === 200 || status === 201) {
          message.success('Campaign stopped');
          setRefresh(Symbol('s'));
        } else {
          message.error('Failed to stop campaign');
        }
      },
    });
  }, [token]);

  const handleUploadCsv = React.useCallback(async () => {
    if (!token || csvFileList.length === 0) return;
    setUploading(true);
    try {
      const [response, status] = await apiImportMarketingCampaignCsv(
        token,
        csvFileList[0] as unknown as File,
      );
      if (status === 200 || status === 201) {
        message.success(
          `Imported ${response.created} contact(s). Skipped: ${response.skippedInvalid} invalid, ${response.skippedDuplicate} duplicate, ${response.skippedNoUser} no matching user, ${response.skippedPaidPlan} already paid, ${response.skippedAlreadyEnrolled} already enrolled.`,
        );
        setCsvFileList([]);
        setRefresh(Symbol('s'));
      } else {
        message.error('Failed to import CSV');
      }
    } catch (e) {
      message.error('An error occurred during import');
    } finally {
      setUploading(false);
    }
  }, [token, csvFileList]);

  const uploadProps: UploadProps = {
    fileList: csvFileList,
    beforeUpload: (file) => {
      const isCsv = file.type === 'text/csv' || file.name.endsWith('.csv');
      if (!isCsv) {
        message.error('You can only upload CSV files!');
        return false;
      }
      setCsvFileList([file]);
      return false;
    },
    onRemove: () => setCsvFileList([]),
    maxCount: 1,
  };

  const handleSendTestEmail = React.useCallback(
    async (step: number) => {
      if (!token || !testEmail) return;
      setSendingTestStep(step);
      const [, status] = await apiSendTestMarketingCampaignEmail(token, {
        email: testEmail,
        step,
      });
      if (status === 200 || status === 201) {
        message.success(`Step ${step} test email sent to ${testEmail}`);
      } else {
        message.error(`Failed to send step ${step} test email`);
      }
      setSendingTestStep(null);
    },
    [token, testEmail],
  );

  const handleDeleteContact = React.useCallback(
    async (contactAliasId: string) => {
      if (!token) return;
      const [, status] = await apiDeleteMarketingContact(token, contactAliasId);
      if (status === 200 || status === 201) {
        message.success('Contact deleted');
        setRefresh(Symbol('s'));
      } else {
        message.error('Failed to delete contact');
      }
    },
    [token],
  );

  const columns = React.useMemo<ColumnsType<ContactRow>>(
    () => [
      { key: 'email', title: 'Email', dataIndex: 'email' },
      {
        key: 'name',
        title: 'Name',
        render: (_, record) =>
          [record.firstName, record.lastName].filter(Boolean).join(' ') || '-',
      },
      { key: 'currentStep', title: 'Step', dataIndex: 'currentStep' },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        render: (v: MarketingContactStatus) => <Tag>{v}</Tag>,
      },
      {
        key: 'nextSendAt',
        title: 'Next send',
        dataIndex: 'nextSendAt',
        render: (v: string | null) => (v ? dayjs(v).format('MMM D, HH:mm') : '-'),
      },
      {
        key: 'lastSentAt',
        title: 'Last sent',
        dataIndex: 'lastSentAt',
        render: (v: string | null) => (v ? dayjs(v).format('MMM D, HH:mm') : '-'),
      },
      {
        key: 'actions',
        title: 'Actions',
        render: (_, record) => (
          <Popconfirm
            title="Delete this contact?"
            description="They will stop receiving campaign emails and be removed from the list."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteContact(record.aliasId)}
          >
            <Button danger size="small">
              Delete
            </Button>
          </Popconfirm>
        ),
      },
    ],
    [handleDeleteContact],
  );

  const settings = data?.settings;

  return (
    <Card bordered={false} title="September Reactivation Email Campaign">
      {settings ? (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={4}>
              <Card size="small">
                <Statistic title="Status" value={settings.status.toUpperCase()} />
                <Tag color={STATUS_COLORS[settings.status]} style={{ marginTop: 8 }}>
                  {settings.status}
                </Tag>
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="Sent today"
                  value={data.sentToday}
                  suffix={`/ ${settings.dailyCap}`}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="Active"
                  value={data.counts[MarketingContactStatus.ACTIVE] || 0}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="Replied"
                  value={data.counts[MarketingContactStatus.REPLIED] || 0}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="Converted"
                  value={data.counts[MarketingContactStatus.CONVERTED] || 0}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="Unsubscribed"
                  value={data.counts[MarketingContactStatus.UNSUBSCRIBED] || 0}
                />
              </Card>
            </Col>
          </Row>

          <div style={{ marginBottom: 24, display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              disabled={settings.status === MarketingCampaignStatus.RUNNING}
              onClick={handleStart}
            >
              Start
            </Button>
            <Button
              disabled={settings.status !== MarketingCampaignStatus.RUNNING}
              onClick={handlePause}
            >
              Pause
            </Button>
            <Button
              danger
              disabled={settings.status === MarketingCampaignStatus.STOPPED}
              onClick={handleStop}
            >
              Stop
            </Button>
          </div>

          <Typography.Title level={4}>Settings</Typography.Title>
          <Form
            form={form}
            layout="inline"
            onFinish={handleUpdateSettings}
            disabled={saving}
            style={{ marginBottom: 32, rowGap: 16 }}
          >
            <Form.Item
              label="Daily cap"
              name="dailyCap"
              initialValue={settings.dailyCap}
            >
              <InputNumber min={1} />
            </Form.Item>
            <Form.Item
              label="Allowed days"
              name="allowedDaysOfWeek"
              initialValue={settings.allowedDaysOfWeek}
            >
              <Checkbox.Group options={DAYS_OF_WEEK} />
            </Form.Item>
            <Form.Item
              label="Send window start (UTC hour)"
              name="sendWindowStartHour"
              initialValue={settings.sendWindowStartHour}
            >
              <InputNumber min={0} max={23} />
            </Form.Item>
            <Form.Item
              label="Send window end (UTC hour)"
              name="sendWindowEndHour"
              initialValue={settings.sendWindowEndHour}
            >
              <InputNumber min={0} max={23} />
            </Form.Item>
            <Form.Item
              label="Wave start date"
              name="waveStartDate"
              initialValue={
                settings.waveStartDate ? dayjs(settings.waveStartDate) : null
              }
            >
              <DatePicker />
            </Form.Item>
            <Form.Item
              label="Deadline date"
              name="deadlineDate"
              initialValue={
                settings.deadlineDate ? dayjs(settings.deadlineDate) : null
              }
            >
              <DatePicker />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Save Changes
              </Button>
            </Form.Item>
          </Form>

          <Typography.Title level={4}>Import Contacts</Typography.Title>
          <Card size="small" style={{ marginBottom: 32, maxWidth: 480 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Typography.Text type="secondary">
                Upload a CSV with `email`, `first_name`, `last_name`, `timezone`
                columns. Rows are deduped, matched to existing users, and users
                already on a paid plan or already enrolled are skipped.
              </Typography.Text>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>Select CSV File</Button>
              </Upload>
              <Button
                type="primary"
                onClick={handleUploadCsv}
                disabled={csvFileList.length === 0}
                loading={uploading}
              >
                {uploading ? 'Importing...' : 'Import Contacts'}
              </Button>
            </Space>
          </Card>

          <Typography.Title level={4}>Test Emails</Typography.Title>
          <Card size="small" style={{ marginBottom: 32, maxWidth: 480 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Typography.Text type="secondary">
                Send a one-off test of any step&apos;s email to an address of
                your choosing, so you can check how it renders in your inbox.
                This does not affect any contact or send stats.
              </Typography.Text>
              <Input
                placeholder="you@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
              <Space wrap>
                {CAMPAIGN_STEPS.map((step) => (
                  <Button
                    key={step}
                    onClick={() => handleSendTestEmail(step)}
                    disabled={!testEmail}
                    loading={sendingTestStep === step}
                  >
                    Send Step {step}
                  </Button>
                ))}
              </Space>
            </Space>
          </Card>

          <Typography.Title level={4}>Contacts</Typography.Title>
          <Select
            allowClear
            placeholder="Filter by status"
            style={{ width: 200, marginBottom: 16 }}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setOffset(0);
            }}
            options={Object.values(MarketingContactStatus).map((s) => ({
              label: s,
              value: s,
            }))}
          />
          <Table
            loading={contactsLoading}
            scroll={{ x: true }}
            dataSource={contacts?.[0] || []}
            columns={columns}
            pagination={{
              total: contacts?.[1] || 0,
              current: offset / PER_PAGE_LIMIT + 1,
              pageSize: PER_PAGE_LIMIT,
            }}
            onChange={(pagination) => {
              const current = pagination.current || 1;
              setOffset((current - 1) * PER_PAGE_LIMIT);
            }}
          />
        </>
      ) : null}
    </Card>
  );
}

export default EmailCampaign;
